import config from '@/config/index';
import { ErrorCode } from '@/types/error-code.enum';
import { AppError } from '@/utils/app-error';
import { FallbackProps } from 'react-error-boundary';

interface Content {
  title: string;
  body: string;
}

const CodeToContentMap: Record<ErrorCode, Content> = {
  [ErrorCode.IncompleteLink]: {
    title: 'Hoppsan! Länken är trasig',
    body: 'Det verkar som att webshops-länken är ogitlig. Det kan exempelvis bero på att delar av länken har gått förlorad. Kontakta webshopens ägare för att få en ny länk.',
  },
  [ErrorCode.InvalidShopCode]: {
    title: 'Ogiltig webshop-kod',
    body: 'Webshop-koden i länken är ogiltig eller så finns ingen webshop kopplad till den. Kontrollera länken och försök igen, eller kontakta webshopens ägare för en ny länk.',
  },
  [ErrorCode.InvalidWebshop]: {
    title: 'Ogiltig webshop',
    body: 'Det verkar som att webshopen är ogitlig. Det kan exempelvis bero på att länken blivit skadad, eller att gruppen är nedstängd. Kontakta webshopens ägare för att få en ny länk.',
  },
  [ErrorCode.SaleNotActive]: {
    title: 'Försäljning inte aktiv',
    body: 'Försäljningen webshopen är ämnad för är inte aktiv. Dubbelkolla med webshopens ägare att de fortfarande säljer, och be om en ny länk.',
  },
};

const defaultContent: Content = {
  title: 'Ett problem uppstod!',
  body: 'Det kan bero på ett tillfälligt tekniskt problem eller en störning i tjänsten. Vi rekommenderar att du försöker igen om en liten stund.',
};

export default function Error({ error, resetErrorBoundary }: FallbackProps) {
  return (
    <div className="flex h-[100dvh] overflow-hidden">
      {/* Content area */}
      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden bg-white dark:bg-gray-900">
        <main className="grow">
          {/* Page header */}

          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            <div className="max-w-2xl m-auto mt-16">
              <div className="text-center px-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-red-200 to-red-100 dark:from-red-700 dark:to-red-800 mb-4">
                  <svg className="w-5 h-6 fill-current" viewBox="0 0 20 24">
                    <path
                      className="text-red-500 dark:text-red-600"
                      d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                    />
                    <path
                      className="text-red-300 dark:text-red-400"
                      d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                    />
                    <path
                      className="text-red-400 dark:text-red-500"
                      d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl text-gray-800 dark:text-gray-100 font-bold mb-2">
                  {error instanceof AppError ? CodeToContentMap[error.code].title : defaultContent.title}
                </h2>
                <div className="mb-6">
                  {error instanceof AppError ? CodeToContentMap[error.code].body : defaultContent.body}
                </div>
                <div className="mb-6">
                  Om det inte går att lösa, kontakta oss på{' '}
                  <a href={config.links.landing} className="underline text-gray-700 hover:text-gray-800 cursor-pointer">
                    Piggo.se
                  </a>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
